import { actionCableUrl } from "@/utils/meta";
import { type ReasonError, type Transport } from "@anycable/core";
import { createCable } from "@anycable/web";
import * as Sentry from "@sentry/vue";

// Mutator client for the generated cable services, mirroring internalAxiosClient
// for the Orval REST client: the single place that configures the transport, so
// the generated code depends only on this file's `getCable` export.
//
// AnyCable's client speaks the vanilla ActionCable protocol
// (actioncable-v1-json), so this works against the existing Rails ActionCable
// server with no backend changes.

type Cable = ReturnType<typeof createCable>;

let cable: Cable | null = null;

// Where a visitor renewal (see `bindConnectionState`) stands: `pending` from the
// refusal until the renewed handshake goes out, `sent` once it has. Only a
// refusal in `sent` is the renewed visitor being refused; one in `pending` is
// a late duplicate of the refusal that started the renewal.
let visitorRenewal: "idle" | "pending" | "sent" = "idle";

// Rails' `action_cable_meta_tag` renders a root-relative path (`/cable`). The
// WebSocket constructor resolves that against the document, but
// `Transport#setParam` runs it through `new URL()`, which throws on a relative
// path — so the identity below could never be applied. AnyCable absolutizes
// only the URL it derives from the DOM itself, never one passed to
// `createCable`, so we do the same conversion here.
const absoluteCableUrl = (): string => {
  const url = actionCableUrl();

  if (/^wss?:\/\//.test(url)) return url;

  return `${window.location.protocol.replace("http", "ws")}//${window.location.host}${url}`;
};

// Identity is resolved here rather than baked into the URL at construction
// time. A public page subscribes from its `<script setup>`, which runs before
// `<WithVisitor>`'s `onMounted` has created the visitor, so a URL fixed at
// construction carries no `visitor_token` and the server rejects the whole
// connection for the tab's lifetime (#11539). AnyCable awaits this configurator
// before every `transport.open()`, so waiting here delays the handshake until an
// identity exists and re-applies it on each reconnect.
const configureIdentity = async (transport: Transport): Promise<void> => {
  try {
    await ensureVisitorSession();
  } catch (error: unknown) {
    // Connect anyway: the server decides. A visitor that could not be created
    // is a rejected connection either way, and throwing here would leave the
    // cable stuck in `connecting` instead of surfacing a close.
    Sentry.captureException(error, {
      tags: { component: "internalCableClient", phase: "identity" },
    });
  }

  const visitorToken = useUserStore().visitorToken;

  if (visitorToken) transport.setParam("visitor_token", visitorToken);

  if (visitorRenewal === "pending") visitorRenewal = "sent";
};

// A `close` is terminal: the server refused the handshake
// (`reject_unauthorized_connection` sends `reconnect: false`) or the monitor
// gave up reconnecting. Either way every subscription on the tab is dead and
// nothing brings it back, so the banner stays until the user reloads. A
// transient drop emits `disconnect` instead and AnyCable reconnects from it —
// that must not raise anything. A `close` without an error is a deliberate
// teardown.
//
// `unauthorized` is the exception (#13259): the tab's identity is gone — a
// logout in another tab, an expired session, a pupil logged out by the
// teacher, a visitor removed overnight. That is a session state, not a broken
// connection, so it reaches neither the banner nor a Sentry issue (only a
// breadcrumb). A signed-in tab is
// sent to login by its next API call's 401. A visitor can always get a new
// identity, so the tab mints one and reconnects — once per streak, so a server
// that keeps refusing cannot loop. AnyCable resubscribes the closed
// subscriptions on `connect()`. Further refusals before the renewed handshake
// goes out are duplicates of the first and are ignored. If the renewed attempt
// itself is refused (the visitor could not be created, or the server still
// rejects it), the cable is genuinely dead: it falls through to the banner,
// whose reload is the recovery.
//
// The monitor can emit several closes in a row, so Sentry hears about one
// streak once; a cable that reconnects and dies again is a new streak.
const bindConnectionState = (target: Cable): void => {
  let reported = false;

  target.on("close", (event?: ReasonError) => {
    if (!event) return;

    if (event.reason === "unauthorized" && visitorRenewal !== "sent") {
      // Not an error, but leave a trace: if REST auth works and the cable
      // keeps refusing, the next reported error carries this breadcrumb.
      Sentry.addBreadcrumb({
        category: "cable",
        message: "Cable refused as unauthorized",
        level: "info",
      });

      if (visitorRenewal === "pending") return;

      if (useUserStore().isVisitor) {
        visitorRenewal = "pending";
        forgetVisitorSession();
        target.connect().catch(() => {});
      }
      return;
    }

    useCableConnectionStore().markLost();

    if (reported) return;
    reported = true;
    Sentry.captureException(event, {
      tags: {
        component: "internalCableClient",
        phase: "connection",
        reason: event.reason ?? "unknown",
      },
    });
  });

  target.on("connect", () => {
    reported = false;
    visitorRenewal = "idle";
    useCableConnectionStore().markConnected();
  });
};

/** Lazily create the singleton AnyCable client used by the generated cable services. */
export function getCable(): Cable {
  if (cable) return cable;

  cable = createCable(absoluteCableUrl(), {
    transportConfigurator: configureIdentity,
  });
  bindConnectionState(cable);

  return cable;
}
